// ===============================================================
// Forms — Preview + FormRenderer
// Internal preview page (requires auth) + reusable FormRenderer
// ===============================================================

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Title, Text, TextInput, NumberInput, Textarea, Select,
  MultiSelect, Checkbox, Radio, Button, Group, Stack, Divider,
  Loader, Center, CopyButton, ActionIcon, Tooltip, FileButton,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconCopy, IconCheck, IconCamera, IconMapPin,
  IconStar, IconStarFilled, IconChevronLeft, IconChevronRight,
} from '@tabler/icons-react';
import { useFormsStore } from '../../store/formsStore';
import type { FormSchema, FormField } from '../../types/forms';
import { notifications } from '../../lib/notifications';
import { primary } from '../../lib/colors';

// ═════════════════════════════════════════════════════════════════
// FormRenderer — reusable component that renders any FormSchema
// ═════════════════════════════════════════════════════════════════

interface FormRendererProps {
  schema: FormSchema;
  onSubmit: (data: Record<string, unknown>) => void;
  readOnly?: boolean;
}

export function FormRenderer({ schema, onSubmit, readOnly }: FormRendererProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sigCanvasRef = useRef<Record<string, HTMLCanvasElement | null>>({});
  const sigDrawing = useRef<Record<string, boolean>>({});

  const pages = (schema.pages || []).length > 0 ? schema.pages : [{ title: 'Page 1', fields: [] as FormField[] }];
  const page = pages[currentPage] || pages[0];
  const isLastPage = currentPage === pages.length - 1;

  const setValue = useCallback((key: string, val: unknown) => {
    setValues(prev => ({ ...prev, [key]: val }));
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  }, []);

  // ── Validate current page ──────────────────────────────────
  const validatePage = (): boolean => {
    const errs: Record<string, string> = {};
    (page.fields || []).forEach(f => {
      if (f.type === 'section' || f.type === 'instructions' || f.type === 'page_break') return;
      if (f.required) {
        const v = values[f.key];
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) {
          errs[f.key] = 'This field is required';
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (!validatePage()) return;
    setCurrentPage(p => Math.min(p + 1, pages.length - 1));
  };

  const handlePrev = () => setCurrentPage(p => Math.max(p - 1, 0));

  const handleSubmit = () => {
    if (!validatePage()) return;
    onSubmit(values);
  };

  // ── GPS Capture ─────────────────────────────────────────────
  const captureGPS = (key: string) => {
    if (!navigator.geolocation) {
      setValue(key, { lat: 0, lng: 0, error: 'Geolocation not supported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => setValue(key, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      err => setValue(key, { error: err.message }),
      { enableHighAccuracy: true }
    );
  };

  // ── Photo Capture ───────────────────────────────────────────
  const capturePhoto = (key: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => setValue(key, reader.result as string);
      reader.readAsDataURL(file);
    };
    input.click();
  };

  // ── Signature Canvas ────────────────────────────────────────
  const initSigCanvas = (key: string, canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    sigCanvasRef.current[key] = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#19009B';
    ctx.lineCap = 'round';

    const getPos = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      if ('touches' in e) {
        return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
      }
      return { x: (e as MouseEvent).clientX - rect.left, y: (e as MouseEvent).clientY - rect.top };
    };

    const start = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      sigDrawing.current[key] = true;
      const { x, y } = getPos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!sigDrawing.current[key]) return;
      e.preventDefault();
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const end = () => {
      sigDrawing.current[key] = false;
      setValue(key, canvas.toDataURL());
    };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
  };

  const clearSignature = (key: string) => {
    const canvas = sigCanvasRef.current[key];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setValue(key, '');
  };

  // ── Conditional visibility ──────────────────────────────────
  const isVisible = (f: FormField): boolean => {
    if (!f.conditional) return true;
    const depVal = values[f.conditional.fieldKey];
    const str = String(depVal ?? '');
    switch (f.conditional.operator) {
      case 'equals': return str === f.conditional.value;
      case 'not_equals': return str !== f.conditional.value;
      case 'contains': return str.includes(f.conditional.value);
      case 'not_empty': return str !== '';
      case 'empty': return str === '';
      default: return true;
    }
  };

  // ── Render single field ─────────────────────────────────────
  const renderField = (f: FormField) => {
    if (!isVisible(f)) return null;
    const val = values[f.key];
    const err = errors[f.key];
    const disabled = readOnly;

    switch (f.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <TextInput
            key={f.key}
            label={f.label}
            placeholder={f.placeholder}
            description={f.helpText}
            required={f.required}
            type={f.type === 'text' ? 'text' : f.type}
            value={(val as string) ?? ''}
            onChange={e => setValue(f.key, e.target.value)}
            error={err}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <NumberInput
            key={f.key}
            label={f.label}
            placeholder={f.placeholder}
            description={f.helpText}
            required={f.required}
            value={(val as number) ?? ''}
            onChange={v => setValue(f.key, v)}
            min={f.validation?.min}
            max={f.validation?.max}
            error={err}
            disabled={disabled}
          />
        );

      case 'date':
        return (
          <TextInput
            key={f.key}
            label={f.label}
            description={f.helpText}
            required={f.required}
            type="date"
            value={(val as string) ?? ''}
            onChange={e => setValue(f.key, e.target.value)}
            error={err}
            disabled={disabled}
          />
        );

      case 'textarea':
        return (
          <Textarea
            key={f.key}
            label={f.label}
            placeholder={f.placeholder}
            description={f.helpText}
            required={f.required}
            minRows={3}
            value={(val as string) ?? ''}
            onChange={e => setValue(f.key, e.target.value)}
            error={err}
            disabled={disabled}
          />
        );

      case 'select':
        return (
          <Select
            key={f.key}
            label={f.label}
            placeholder={f.placeholder || 'Select...'}
            description={f.helpText}
            required={f.required}
            data={(f.options || []).filter(o => o && o.value != null && o.label != null).map(o => ({ value: String(o.value), label: String(o.label) }))}
            value={(val as string) ?? null}
            onChange={v => setValue(f.key, v)}
            error={err}
            disabled={disabled}
            clearable
          />
        );

      case 'multiselect':
        return (
          <MultiSelect
            key={f.key}
            label={f.label}
            placeholder={f.placeholder || 'Select...'}
            description={f.helpText}
            required={f.required}
            data={(f.options || []).filter(o => o && o.value != null && o.label != null).map(o => ({ value: String(o.value), label: String(o.label) }))}
            value={(val as string[]) ?? []}
            onChange={v => setValue(f.key, v)}
            error={err}
            disabled={disabled}
            clearable
          />
        );

      case 'checkbox':
        return (
          <Checkbox.Group
            key={f.key}
            label={f.label}
            description={f.helpText}
            value={(val as string[]) ?? []}
            onChange={v => setValue(f.key, v)}
            error={err}
          >
            <Stack gap="xs" mt={6}>
              {(f.options || []).filter(o => o && o.value != null).map(o => (
                <Checkbox key={String(o.value)} value={String(o.value)} label={String(o.label ?? '')} disabled={disabled} />
              ))}
            </Stack>
          </Checkbox.Group>
        );

      case 'radio':
        return (
          <Radio.Group
            key={f.key}
            label={f.label}
            description={f.helpText}
            value={(val as string) ?? ''}
            onChange={v => setValue(f.key, v)}
            error={err}
          >
            <Stack gap="xs" mt={6}>
              {(f.options || []).filter(o => o && o.value != null).map(o => (
                <Paper key={String(o.value)} p="sm" radius="sm" withBorder
                  style={{
                    cursor: disabled ? 'default' : 'pointer',
                    borderColor: val === o.value ? 'var(--color-primary-700)' : undefined,
                    background: val === o.value ? 'rgba(75, 63, 255, 0.04)' : undefined,
                  }}
                  onClick={() => !disabled && setValue(f.key, String(o.value))}
                >
                  <Radio value={String(o.value)} label={String(o.label ?? '')} disabled={disabled} />
                </Paper>
              ))}
            </Stack>
          </Radio.Group>
        );

      case 'photo':
        return (
          <div key={f.key}>
            <Text size="sm" fw={500}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed">{f.helpText}</Text>}
            {typeof val === 'string' && val.startsWith('data:image') && (
              <img src={val} alt="Captured" style={{ maxWidth: 240, borderRadius: 4, marginTop: 8, marginBottom: 8 }} />
            )}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconCamera size={14} />}
              onClick={() => capturePhoto(f.key)}
              disabled={disabled}
              mt={4}
            >
              {val ? 'Retake Photo' : 'Capture Photo'}
            </Button>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );

      case 'gps':
        return (
          <div key={f.key}>
            <Text size="sm" fw={500}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed">{f.helpText}</Text>}
            {!!val && typeof val === "object" && "lat" in (val as Record<string, unknown>) && (
              <Text size="sm" mt={4} ff="monospace">
                {(val as { lat: number; lng: number }).lat.toFixed(6)}, {(val as { lat: number; lng: number }).lng.toFixed(6)}
              </Text>
            )}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconMapPin size={14} />}
              onClick={() => captureGPS(f.key)}
              disabled={disabled}
              mt={4}
            >
              {val ? 'Update Location' : 'Capture GPS'}
            </Button>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );

      case 'signature':
        return (
          <div key={f.key}>
            <Text size="sm" fw={500}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed">{f.helpText}</Text>}
            <Paper withBorder radius="sm" mt={4} p={0} style={{ overflow: 'hidden' }}>
              <canvas
                ref={el => initSigCanvas(f.key, el)}
                width={400}
                height={150}
                style={{ display: 'block', width: '100%', maxWidth: 400, height: 150, background: '#fafafa', touchAction: 'none' }}
              />
            </Paper>
            <Button variant="subtle" size="xs" mt={4} onClick={() => clearSignature(f.key)} disabled={disabled}>
              Clear
            </Button>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );

      case 'file':
        return (
          <div key={f.key}>
            <Text size="sm" fw={500}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed">{f.helpText}</Text>}
            {!!val && <Text size="xs" mt={4} c="var(--color-success)">File selected</Text>}
            <FileButton onChange={file => {
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => setValue(f.key, reader.result);
              reader.readAsDataURL(file);
            }}>
              {(props) => (
                <Button variant="light" size="xs" mt={4} disabled={disabled} {...props}>
                  {val ? 'Change File' : 'Upload File'}
                </Button>
              )}
            </FileButton>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );

      case 'rating': {
        const stars = 5;
        const current = Number(val) || 0;
        return (
          <div key={f.key}>
            <Text size="sm" fw={500}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed">{f.helpText}</Text>}
            <Group gap={4} mt={6}>
              {Array.from({ length: stars }, (_, i) => (
                <ActionIcon
                  key={i}
                  variant="transparent"
                  color={i < current ? '#C68700' : '#939598'}
                  onClick={() => !disabled && setValue(f.key, i + 1)}
                  size="lg"
                >
                  {i < current ? <IconStarFilled size={24} /> : <IconStar size={24} />}
                </ActionIcon>
              ))}
              {current > 0 && <Text size="sm" c="dimmed" ml={4}>{current}/5</Text>}
            </Group>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );
      }

      case 'likert': {
        const scaleOptions = ((f.config?.likertScale) as string[]) || ['1', '2', '3', '4', '5'];
        const items = (f.options || []).length > 0 ? (f.options || []) : [{ value: f.key, label: f.label }];
        const likertVals = (val as Record<string, string>) || {};
        return (
          <div key={f.key}>
            <Text size="sm" fw={500} mb={8}>{f.label}{f.required && <span style={{ color: 'var(--color-error)' }}> *</span>}</Text>
            {f.helpText && <Text size="xs" c="dimmed" mb={4}>{f.helpText}</Text>}
            <Paper withBorder radius="sm" p="sm" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontSize: 12 }}></th>
                    {scaleOptions.map(s => (
                      <th key={s} style={{ textAlign: 'center', padding: '4px 8px', fontSize: 12 }}>{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.value}>
                      <td style={{ padding: '8px 8px', fontSize: 14 }}>{item.label}</td>
                      {scaleOptions.map(s => (
                        <td key={s} style={{ textAlign: 'center', padding: '8px 8px' }}>
                          <Radio
                            name={`likert-${f.key}-${item.value}`}
                            value={s}
                            checked={likertVals[item.value] === s}
                            onChange={() => {
                              if (disabled) return;
                              setValue(f.key, { ...likertVals, [item.value]: s });
                            }}
                            disabled={disabled}
                            size="xs"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Paper>
            {err && <Text size="xs" c="var(--color-error)" mt={4}>{err}</Text>}
          </div>
        );
      }

      case 'section':
        return (
          <Divider
            key={f.key}
            label={<Text size="sm" fw={600}>{f.label}</Text>}
            labelPosition="left"
            my="sm"
          />
        );

      case 'instructions':
        return (
          <Paper key={f.key} p="sm" radius="sm" bg="blue.0" withBorder={false}>
            <Text size="sm" c="dimmed">{f.label}</Text>
          </Paper>
        );

      case 'page_break':
        return null; // handled by page system

      default:
        return null;
    }
  };

  // ── Progress dots ───────────────────────────────────────────
  const renderProgress = () => {
    if (pages.length <= 1) return null;
    return (
      <Group justify="center" gap={8} mb="md">
        {pages.map((_, i) => (
          <Box
            key={i}
            w={i === currentPage ? 24 : 8}
            h={8}
            style={{
              borderRadius: 4,
              background: i <= currentPage ? 'var(--color-primary-700)' : '#E0E0E0',
              transition: 'width 0.2s, background 0.2s',
            }}
          />
        ))}
      </Group>
    );
  };

  return (
    <Box maw={680} mx="auto">
      {/* Branding */}
      <Text size="xs" fw={600} c={primary[700]} tt="uppercase" style={{ letterSpacing: 1 }} mb={4}>
        OpenGov Emergency Management
      </Text>
      <Title order={3} mb={4}>{schema.title || 'Untitled Form'}</Title>
      {schema.description && <Text size="sm" c="dimmed" mb="md">{schema.description}</Text>}

      {renderProgress()}

      {/* Page title */}
      {page.title && pages.length > 1 && (
        <Text size="sm" fw={600} c="dimmed" mb="sm">
          {page.title} ({currentPage + 1} of {pages.length})
        </Text>
      )}

      {/* Fields */}
      <Stack gap={16}>
        {(page.fields || []).map(f => renderField(f))}
      </Stack>

      {/* Navigation */}
      <Group justify="space-between" mt="xl">
        <Button
          variant="default"
          leftSection={<IconChevronLeft size={14} />}
          onClick={handlePrev}
          disabled={currentPage === 0}
        >
          Previous
        </Button>

        {isLastPage ? (
          <Button
            color={primary[700]}
            onClick={handleSubmit}
            disabled={readOnly}
          >
            {schema.settings?.submitButtonText || 'Submit'}
          </Button>
        ) : (
          <Button
            variant="light"
            color={primary[700]}
            rightSection={<IconChevronRight size={14} />}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Group>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════
// FormPreview — page that loads a template and renders it
// ═════════════════════════════════════════════════════════════════

export default function FormPreview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { templates, loading, loadTemplates } = useFormsStore();

  useEffect(() => { loadTemplates(); }, []);

  const template = templates.find(t => String(t.Rid) === id || String(t.id) === id);

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  if (!template) {
    return (
      <Center p="xl">
        <Stack align="center" gap="sm">
          <Text c="dimmed">Template not found</Text>
          <Button variant="subtle" onClick={() => navigate('/forms/templates')}>Back to Templates</Button>
        </Stack>
      </Center>
    );
  }

  let schema: FormSchema;
  const rawSchema = (template as any).schemaJson || (template as any).schema || '';
  try {
    if (!rawSchema.trim()) throw new Error('empty');
    schema = JSON.parse(rawSchema);
    if (!schema || !Array.isArray(schema.pages)) throw new Error('no pages');
    // Ensure settings exists with defaults
    if (!schema.settings) {
      schema.settings = {
        submitButtonText: 'Submit',
        successMessage: 'Thank you! Your response has been recorded.',
        allowMultipleSubmissions: false,
        notifyOnSubmission: false,
        notifyEmail: '',
      };
    }
  } catch {
    return (
      <Center p="xl">
        <Stack align="center" gap="sm">
          <Text c="#F86A0B" fw={500}>No form content yet</Text>
          <Text c="dimmed" size="sm">Open the Form Builder to add fields to this template.</Text>
          <Button variant="subtle" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(`/forms/builder/${id}`)}>
            Go to Builder
          </Button>
        </Stack>
      </Center>
    );
  }

  const publicUrl = template.isPublic === 'true' && template.publicSlug
    ? `${window.location.origin}/forms/public/${template.publicSlug}`
    : null;

  return (
    <Box p="md">
      {/* Top bar */}
      <Group justify="space-between" mb="lg">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate(`/forms/builder/${id}`)}
          size="sm"
        >
          Back to Builder
        </Button>

        {publicUrl && (
          <CopyButton value={publicUrl}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied!' : 'Copy public link'}>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  color={copied ? 'var(--color-success)' : primary[700]}
                  onClick={copy}
                >
                  {copied ? 'Copied' : 'Copy Public Link'}
                </Button>
              </Tooltip>
            )}
          </CopyButton>
        )}
      </Group>

      {/* Banner */}
      <Paper p="sm" mb="lg" radius="sm" bg="blue.0" ta="center">
        <Text size="xs" fw={500} c="var(--color-primary-700)">
          Preview Mode — submissions will not be saved
        </Text>
      </Paper>

      {/* Renderer */}
      <FormRenderer
        schema={schema}
        onSubmit={() => {
          notifications.show({
            title: 'Preview Submitted',
            message: schema.settings?.successMessage || 'Form submitted successfully (preview only).',
            color: 'var(--color-success)',
          });
        }}
      />
    </Box>
  );
}
